import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Edit3, Save, X, Lock, ShieldAlert } from "lucide-react";

/**
 * TimelinePanel — 13-point matrix, editable by Ops/CRM/Director/Admin.
 * If 45% not paid, shows a locked state.
 * If timeline not computed yet, offers "Compute now" for Ops/Director/Admin/Design leads.
 */
export default function TimelinePanel({ projectId, project, canEdit, canCompute, onDirty }) {
  const [timeline, setTimeline] = useState(null);
  const [proj, setProj] = useState(project || null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setProj(project || null); }, [project]);
  const paid = !!proj?.is_45_percent_paid;

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get(`/timeline/${projectId}`).catch(() => ({ data: null })),
        api.get(`/design/projects/${projectId}`).catch(() => ({ data: null })),
      ]);
      setTimeline(t.data);
      if (p.data) setProj(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const startEdit = () => {
    const d = {};
    (timeline?.points || []).forEach((p) => { d[p.key] = p.date; });
    setDraft(d);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft({}); };

  const save = async () => {
    setSaving(true);
    try {
      const changed = (timeline?.points || []).filter((p) => draft[p.key] && draft[p.key] !== p.date)
        .map((p) => ({ key: p.key, date: draft[p.key] }));
      if (changed.length === 0) { setEditing(false); return; }
      await api.patch(`/timeline/${projectId}`, { points: changed, reason: "Manual override" });
      toast.success("Timeline updated");
      setEditing(false);
      await load();
      onDirty?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const compute = async () => {
    try {
      await api.post(`/timeline/compute/${projectId}`, {});
      toast.success("Timeline computed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPaid = async () => {
    try {
      await api.post(`/design/projects/${projectId}/finance-45`, { is_paid: true });
      toast.success("45% milestone marked paid");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!paid) {
    return (
      <div className="iz-card p-8 text-center" data-testid="timeline-locked">
        <Lock className="w-6 h-6 text-gray-600 mx-auto mb-3" />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Timeline locked</p>
        <p className="text-sm text-gray-400 mb-4">
          The 13-point production timeline unlocks after the <span className="text-[#d4af37]">45% payment milestone</span> is confirmed.
        </p>
        {canCompute && (
          <button onClick={markPaid} className="iz-btn-secondary text-xs" data-testid="mark-45-paid">
            Simulate 45% payment received
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return <div className="iz-card p-8 text-center text-xs font-mono text-gray-600">Loading timeline…</div>;
  }

  if (!timeline) {
    return (
      <div className="iz-card p-8 text-center" data-testid="timeline-empty">
        <CalendarClock className="w-6 h-6 text-[#d4af37] mx-auto mb-3" />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Ready to compute</p>
        <p className="text-sm text-gray-400 mb-4">
          45% is confirmed. Compute the timeline based on the final project value.
        </p>
        {canCompute && (
          <button onClick={compute} className="iz-btn-primary" data-testid="compute-timeline">
            Compute 13-point timeline
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="iz-card overflow-hidden" data-testid="timeline-panel">
      <div className="px-5 py-4 border-b border-[#1a1a1a] flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Universal Timeline</p>
          <h3 className="font-display text-lg text-white mt-1">13-Point Production Matrix</h3>
          <p className="text-[10px] font-mono text-gray-500 mt-1">
            D0 · {timeline.d0} · X = {timeline.X_working_days} working days
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!editing && canEdit && (
            <button onClick={startEdit} className="iz-btn-secondary flex items-center gap-1.5 text-xs" data-testid="edit-timeline">
              <Edit3 className="w-3.5 h-3.5" /> Override dates
            </button>
          )}
          {editing && (
            <>
              <button onClick={cancelEdit} className="iz-btn-secondary flex items-center gap-1.5 text-xs">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={save} disabled={saving} className="iz-btn-primary flex items-center gap-1.5 text-xs" data-testid="save-timeline">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="divide-y divide-[#1a1a1a]">
        {timeline.points.map((p, i) => (
          <div key={p.key} className="grid grid-cols-12 items-center px-5 py-3 gap-3" data-testid={`timeline-row-${p.key}`}>
            <div className="col-span-1 text-[10px] font-mono text-[#d4af37]">{String(i + 1).padStart(2, "0")}</div>
            <div className="col-span-6">
              <p className="text-sm text-white leading-tight">{p.label}</p>
              <p className="text-[10px] font-mono text-gray-600 mt-0.5">
                +{p.offset_working_days} wd {p.overridden && <span className="text-amber-400 ml-1">· overridden</span>}
              </p>
            </div>
            <div className="col-span-5 text-right">
              {editing ? (
                <input
                  type="date"
                  value={draft[p.key] || p.date}
                  onChange={(e) => setDraft({ ...draft, [p.key]: e.target.value })}
                  className="bg-black border border-[#d4af37]/40 rounded-md px-2 py-1 text-xs text-white font-mono focus:border-[#d4af37] outline-none"
                  data-testid={`timeline-date-${p.key}`}
                />
              ) : (
                <p className="font-mono text-sm text-white">{p.date}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {timeline.last_overridden_at && (
        <div className="px-5 py-2 border-t border-[#1a1a1a] flex items-center gap-2 text-[10px] font-mono text-amber-400/80">
          <ShieldAlert className="w-3 h-3" /> Last override · {new Date(timeline.last_overridden_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
